import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder,
  faFile,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFilePowerpoint,
  faFileImage,
  faFileVideo,
  faFileAudio,
  faFileArchive,
  faInfoCircle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import BreadcrumbPath from '../Common/BreadcrumbPath';
import { formatDetailedDate } from '../../utils/dateUtils';
import { filenameIncludesExtension } from '../../utils/nameUtils';
import * as dropboxService from '../../../../services/dropbox';

interface DetailsModalProps {
  item: any;
  type: 'file' | 'folder';
  folders: any[];
  onClose: () => void;
}

const DetailsModal: React.FC<DetailsModalProps> = ({ item, type, folders, onClose }) => {
  const [folderDetails, setFolderDetails] = useState<any>(null);
  const [loadingSize, setLoadingSize] = useState(false);
  const [sizeError, setSizeError] = useState(false);

  // Fetch detailed folder information when modal opens for folders
  useEffect(() => {
    if (type === 'folder' && item.id) {
      fetchFolderDetails();
    }
  }, [type, item.id]);

  const fetchFolderDetails = async () => {
    setLoadingSize(true);
    setSizeError(false);
    try {
      // First try to get folder details
      const folderResponse = await dropboxService.getFolderById(item.id);
      if (folderResponse?.success && folderResponse?.data) {
        setFolderDetails(folderResponse.data);
        
        // If folder details don't include size, try to calculate from files
        if (!folderResponse.data.total_size && !folderResponse.data.size_bytes && !folderResponse.data.size) {
          await calculateFolderSizeFromFiles();
        }
      } else {
        // If folder details API fails, try to calculate from files
        await calculateFolderSizeFromFiles();
      }
    } catch (error) {
      console.error('Error fetching folder details:', error);
      // Try to calculate from files as fallback
      await calculateFolderSizeFromFiles();
    } finally {
      setLoadingSize(false);
    }
  };

  const calculateFolderSizeFromFiles = async () => {
    try {
      // Get all files in the folder
      const filesResponse = await dropboxService.getFiles({ 
        folder_id: item.id,
        include_deleted: false 
      });
      
      if (filesResponse?.success && filesResponse?.data) {
        const files = filesResponse.data;
        let totalSize = 0;
        let fileCount = 0;
        
        files.forEach((file: any) => {
          const fileSize = file.size_bytes || file.size || 0;
          totalSize += typeof fileSize === 'string' ? parseInt(fileSize) : fileSize;
          fileCount++;
        });
        
        // Update folder details with calculated size (even if 0)
        setFolderDetails((prev: any) => ({
          ...prev,
          total_size: totalSize,
          file_count: fileCount,
          calculated: true // Flag to indicate this was calculated
        }));
      } else {
        // If API call fails, assume empty folder
        setFolderDetails((prev: any) => ({
          ...prev,
          total_size: 0,
          file_count: 0,
          calculated: true
        }));
      }
    } catch (error) {
      console.error('Error calculating folder size from files:', error);
      // On error, assume empty folder rather than showing error
      setFolderDetails((prev: any) => ({
        ...prev,
        total_size: 0,
        file_count: 0,
        calculated: true
      }));
    }
  };
  const getFileIcon = (file: any) => {
    const ext = file.extension?.toLowerCase();
    const mime = file.mime_type?.toLowerCase();

    if (mime?.includes('pdf')) return faFilePdf;
    if (mime?.includes('word') || ext === 'doc' || ext === 'docx') return faFileWord;
    if (mime?.includes('excel') || ext === 'xls' || ext === 'xlsx') return faFileExcel;
    if (mime?.includes('powerpoint') || ext === 'ppt' || ext === 'pptx') return faFilePowerpoint;
    if (mime?.includes('image')) return faFileImage;
    if (mime?.includes('video')) return faFileVideo;
    if (mime?.includes('audio')) return faFileAudio;
    if (mime?.includes('zip') || mime?.includes('rar') || mime?.includes('7z')) return faFileArchive;

    return faFile;
  };

  const formatFileSize = (bytes?: number | string) => {
    if (bytes === undefined || bytes === null) return '--';
    const numBytes = typeof bytes === 'string' ? parseInt(bytes) : bytes;
    if (isNaN(numBytes)) return '--';
    if (numBytes === 0) return '0 bytes';
    
    const mb = numBytes / 1024 / 1024;
    if (mb < 1) return `${(numBytes / 1024).toFixed(0)} KB`;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(1)} GB`;
  };

  const getFolderSize = () => {
    if (type === 'file') {
      return formatFileSize(item.size_bytes || item.size);
    }

    // For folders, try different possible size fields
    const detailsSize = folderDetails?.total_size || folderDetails?.size_bytes || folderDetails?.size;
    const itemSize = item.total_size || item.size_bytes || item.size;
    
    if (detailsSize !== undefined && detailsSize !== null) {
      const sizeText = formatFileSize(detailsSize);
      const isCalculated = folderDetails?.calculated;
      return (
        <span>
          {sizeText}
          {isCalculated && (
            <small style={{ color: '#6c757d', marginLeft: '0.5rem', fontSize: '0.75rem' }}>
              (calculated)
            </small>
          )}
        </span>
      );
    }
    
    if (itemSize !== undefined && itemSize !== null) {
      return formatFileSize(itemSize);
    }

    // If we have folder details but no size, it means the folder is empty
    if (folderDetails && !loadingSize) {
      return (
        <span>
          0 bytes
          <small style={{ color: '#6c757d', marginLeft: '0.5rem', fontSize: '0.75rem' }}>
            (empty folder)
          </small>
        </span>
      );
    }

    if (loadingSize) {
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FontAwesomeIcon icon={faSpinner} spin />
          Calculating...
        </span>
      );
    }

    if (sizeError) {
      return (
        <span style={{ color: '#dc3545' }}>
          Unable to calculate size
        </span>
      );
    }

    return 'Calculating...';
  };

  const buildFolderPath = (folderId: string | null) => {
    if (!folderId) return '/';
    const path: string[] = [];
    let currentId = folderId;
    while (currentId) {
      const folder = folders.find((f: any) => f.id === currentId);
      if (folder) {
        path.unshift(folder.name);
        currentId = folder.parent_id;
      } else {
        break;
      }
    }
    return path.length > 0 ? '/' + path.join('/') : '/';
  };

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '600px' }}>
        <div className="modal-content" style={{ border: 'none', borderRadius: '8px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
          <div className="modal-header" style={{ borderBottom: '1px solid #e9ecef', padding: '1rem 1.25rem' }}>
            <h5 className="modal-title" style={{ fontWeight: 600, fontSize: '1.125rem', color: '#212529', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#17a2b8' }} />
              {type === 'folder' ? 'Folder' : 'File'} Details
            </h5>
            <button type="button" className="close" onClick={onClose} style={{ fontSize: '1.5rem', fontWeight: 300, opacity: 0.5 }}>
              <span>&times;</span>
            </button>
          </div>
          <div className="modal-body" style={{ padding: '1.25rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Name */}
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.35rem' }}>
                  Name
                </div>
                <div style={{ 
                  fontSize: '0.95rem', 
                  color: '#212529',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <FontAwesomeIcon 
                    icon={type === 'folder' ? faFolder : getFileIcon(item)} 
                    style={{ color: type === 'folder' ? '#ffc107' : '#17a2b8', flexShrink: 0 }}
                  />
                  <span 
                    style={{ 
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      hyphens: 'auto',
                      flex: 1,
                      minWidth: 0
                    }}
                    title={item.name}
                  >
                    {item.name}
                    {type === 'file' && item.extension && !filenameIncludesExtension(item.name, item.extension) && (
                      <span style={{ color: '#6c757d' }}>.{item.extension}</span>
                    )}
                  </span>
                </div>
              </div>

              {/* Path */}
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.35rem' }}>
                  Location
                </div>
                <div style={{ 
                  background: '#f8f9fa',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #e9ecef'
                }}>
                  <BreadcrumbPath 
                    path={type === 'folder' 
                      ? buildFolderPath(item.parent_id) + item.name
                      : buildFolderPath(item.folder_id)
                    }
                    showIcon={true}
                  />
                </div>
              </div>

              {/* Size */}
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.35rem' }}>
                  {type === 'folder' ? 'Folder Size' : 'File Size'}
                </div>
                <div style={{ fontSize: '0.9rem', color: '#212529', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {getFolderSize()}
                  {type === 'folder' && sizeError && (
                    <button
                      onClick={fetchFolderDetails}
                      style={{
                        background: 'none',
                        border: '1px solid #007bff',
                        color: '#007bff',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        cursor: 'pointer'
                      }}
                      disabled={loadingSize}
                    >
                      Retry
                    </button>
                  )}
                </div>
              </div>

              {/* Additional folder information */}
              {type === 'folder' && folderDetails && (
                <>
                  {folderDetails.file_count !== undefined && (
                    <div>
                      <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.35rem' }}>
                        Contents
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#212529' }}>
                        {folderDetails.file_count || 0} file(s)
                        {folderDetails.folder_count !== undefined && `, ${folderDetails.folder_count || 0} folder(s)`}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Last Modified */}
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.35rem' }}>
                  Last Modified
                </div>
                <div style={{ fontSize: '0.9rem', color: '#212529' }}>
                  {formatDetailedDate(item.updated_at)}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer" style={{ borderTop: '1px solid #e9ecef', padding: '0.75rem 1.25rem', background: '#f8f9fa' }}>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={onClose}
              style={{ 
                padding: '0.5rem 1.25rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                borderRadius: '6px'
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailsModal;
